import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Calendar, RefreshCw, Download } from "lucide-react";
import { api } from "@/lib/api";
import MickeyEars from "./MickeyEars";

const formatDate = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString("pt-BR", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (e) {
        return "—";
    }
};

const toCSV = (rows) => {
    const header = ["Nome", "Convidados", "Mensagem", "Data"];
    const escape = (v) => {
        const s = String(v ?? "").replace(/"/g, '""');
        return `"${s}"`;
    };
    const lines = [header.join(",")];
    for (const r of rows) {
        lines.push(
            [r.name, r.guests, r.message || "", formatDate(r.created_at)]
                .map(escape)
                .join(",")
        );
    }
    return lines.join("\n");
};

export default function Admin() {
    const [rsvps, setRsvps] = useState([]);
    const [stats, setStats] = useState({ total_confirmations: 0, total_guests: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const [list, s] = await Promise.all([
                api.get("/rsvp").then((r) => r.data),
                api.get("/rsvp/stats").then((r) => r.data),
            ]);
            setRsvps(list || []);
            setStats(s);
        } catch (e) {
            setError("Não foi possível carregar as confirmações.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const downloadCSV = () => {
        const csv = toCSV(rsvps);
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rsvps-festa-mickey-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <main
            data-testid="admin-page"
            className="min-h-screen polka px-6 py-12 sm:py-16"
        >
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="flex items-center gap-3 mb-3">
                            <MickeyEars size={36} />
                            <span className="font-heading uppercase tracking-widest text-xs sm:text-sm bg-accent text-accent-foreground rounded-full px-4 py-1">
                                Painel da festa
                            </span>
                        </div>
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold">
                            Quem vem na festa
                        </h1>
                        <p className="text-foreground/70 mt-2">
                            Lista de confirmações do aniversário do Christopher.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            data-testid="admin-refresh-btn"
                            onClick={load}
                            disabled={loading}
                            className="font-heading text-sm bg-white border-thick rounded-full px-5 py-2 shadow-pop-sm hover:-translate-y-0.5 transition-transform disabled:opacity-60 inline-flex items-center gap-2"
                        >
                            <RefreshCw
                                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                            />
                            Atualizar
                        </button>
                        <button
                            data-testid="admin-csv-btn"
                            onClick={downloadCSV}
                            disabled={rsvps.length === 0}
                            className="font-heading text-sm bg-secondary border-thick rounded-full px-5 py-2 shadow-pop-sm hover:-translate-y-0.5 transition-transform disabled:opacity-60 inline-flex items-center gap-2"
                        >
                            <Download className="w-4 h-4" />
                            Baixar CSV
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-10">
                    <div
                        data-testid="admin-stat-confirmations"
                        className="bg-primary text-primary-foreground border-thick rounded-3xl shadow-pop p-6 sm:p-8"
                    >
                        <div className="flex items-center justify-between">
                            <p className="font-heading uppercase tracking-widest text-xs sm:text-sm opacity-90">
                                Confirmações
                            </p>
                            <Calendar className="w-6 h-6" strokeWidth={2.5} />
                        </div>
                        <p className="font-heading text-5xl sm:text-6xl font-bold mt-3 tabular-nums">
                            {stats.total_confirmations}
                        </p>
                    </div>
                    <div
                        data-testid="admin-stat-guests"
                        className="bg-secondary text-secondary-foreground border-thick rounded-3xl shadow-pop p-6 sm:p-8"
                    >
                        <div className="flex items-center justify-between">
                            <p className="font-heading uppercase tracking-widest text-xs sm:text-sm opacity-90">
                                Total de convidados
                            </p>
                            <Users className="w-6 h-6" strokeWidth={2.5} />
                        </div>
                        <p className="font-heading text-5xl sm:text-6xl font-bold mt-3 tabular-nums">
                            {stats.total_guests}
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="bg-white border-thick rounded-2xl p-4 mb-6 text-foreground shadow-pop-sm">
                        {error}
                    </div>
                )}

                {/* List */}
                <div className="bg-white border-thick rounded-3xl shadow-pop overflow-hidden">
                    <div className="px-6 py-4 bg-accent text-accent-foreground border-b-4 border-black flex items-center justify-between">
                        <h2 className="font-heading text-lg uppercase tracking-widest">
                            Lista de confirmações
                        </h2>
                        <span className="font-heading text-sm opacity-80">
                            {rsvps.length} registro(s)
                        </span>
                    </div>

                    {loading && (
                        <div className="p-10 text-center text-foreground/60">
                            Carregando...
                        </div>
                    )}

                    {!loading && rsvps.length === 0 && (
                        <div className="p-10 text-center text-foreground/60">
                            Ainda não há confirmações. Compartilhe o link do
                            convite!
                        </div>
                    )}

                    {!loading && rsvps.length > 0 && (
                        <ul className="divide-y-4 divide-black">
                            {rsvps.map((r, idx) => (
                                <motion.li
                                    key={r.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.03 }}
                                    data-testid={`admin-rsvp-${idx}`}
                                    className="p-6 flex flex-wrap items-start justify-between gap-4 hover:bg-muted/40"
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="font-heading font-bold text-xl">
                                            {r.name}
                                        </p>
                                        {r.message && (
                                            <p className="text-foreground/70 mt-2 max-w-xl">
                                                “{r.message}”
                                            </p>
                                        )}
                                        <p className="font-heading text-xs uppercase tracking-widest text-foreground/50 mt-3">
                                            {formatDate(r.created_at)}
                                        </p>
                                    </div>
                                    <div className="bg-primary text-primary-foreground border-thick rounded-2xl shadow-pop-sm px-4 py-2 font-heading font-bold inline-flex items-center gap-2 shrink-0">
                                        <Users className="w-4 h-4" />
                                        {r.guests}
                                    </div>
                                </motion.li>
                            ))}
                        </ul>
                    )}
                </div>

                <p className="text-center text-foreground/50 text-sm mt-8">
                    Página privada — não compartilhe este link com convidados.
                </p>
            </div>
        </main>
    );
}
